import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { formatTimeRange } from '@modernloop/shared/datetime';
import { LinkIcon } from '@modernloop/shared/icons';
import { GoogleMeetLogo, MicrosoftTeamsLogo, ZoomLogo } from '@modernloop/shared/images';
import { Avatar, Stack, Tooltip, Typography } from '@mui/material';

import { DebriefContentHeader_DebriefFragment } from 'src/generated/mloop-graphql';

import DebriefContentMenu from './DebriefContentMenu';

type Fragments = {
  debrief: DebriefContentHeader_DebriefFragment;
};

type Props = {
  timezone: string;
};

const DebriefContentHeader: FCWithFragments<Fragments, Props> = ({ timezone, debrief }) => {
  const { t } = useTranslation('hiring-dashboard-schedule');
  const locationIcon = useMemo(() => {
    if (debrief.videoMeetingUrl?.toLowerCase().indexOf('zoom') !== -1) {
      return (
        <Tooltip title={t('schedule.content.zoom')}>
          <Avatar src={ZoomLogo} sx={{ width: '16px', height: '16px' }} />
        </Tooltip>
      );
    }

    if (debrief.videoMeetingUrl?.toLowerCase().indexOf('meet.google.com') !== -1) {
      return (
        <Tooltip title={t('schedule.content.googleMeets')}>
          <Avatar src={GoogleMeetLogo} sx={{ width: '16px', height: '16px' }} />
        </Tooltip>
      );
    }

    if (debrief.videoMeetingUrl?.toLowerCase().indexOf('teams.microsoft.com') !== -1) {
      return (
        <Tooltip title={t('schedule.content.microsoftTeams')}>
          <Avatar src={MicrosoftTeamsLogo} sx={{ width: '16px', height: '16px' }} />
        </Tooltip>
      );
    }

    if (debrief.videoMeetingUrl?.length) {
      return <LinkIcon tooltip={t('schedule.content.includesCustomMeetingLink')} fontSize="small" />;
    }

    return null;
  }, [debrief.videoMeetingUrl, t]);

  return (
    <Stack
      data-testid="debrief-content-header"
      data-debrief-id={debrief.id}
      direction="row"
      justifyContent="space-between"
      pl={2.5}
      pr={1.5}
      py={0.5}
    >
      <Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body1">{debrief.name}</Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {formatTimeRange(debrief.startAt, debrief.endAt, timezone, true /* showTzAbbr */)}
          </Typography>

          {locationIcon}
        </Stack>
      </Stack>

      <DebriefContentMenu debrief={debrief} />
    </Stack>
  );
};

DebriefContentHeader.fragments = {
  debrief: gql`
    ${DebriefContentMenu.fragments.debrief}
    fragment DebriefContentHeader_debrief on ApplicationDebrief {
      id
      name
      startAt
      endAt
      videoMeetingUrl
      ...DebriefContentMenu_debrief
    }
  `,
};

export default DebriefContentHeader;
